import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import Footer from './Footer';

const GUIDE_DEFS = [
  { slug: 'migrazione-da-yoast', readTime: '4 min', icon: '🚀' },
  { slug: 'brand-voice',         readTime: '5 min', icon: '🎙️' },
  { slug: 'llms-txt',            readTime: '4 min', icon: '🤖' },
  { slug: 'aeo-vs-seo',          readTime: '6 min', icon: '🧠' },
  { slug: 'cannibalizzazione',   readTime: '5 min', icon: '⚔️' },
];

// 2026-07-14 — l'indice ora elenca le guide dal CPT WP (REST /wp/v2/guides),
// filtrate sulla lingua UI corrente. Polylang free non espone `lang` nel REST:
// la lingua si deriva dal permalink (prefisso /en/ /es/ /fr/ /de/, IT senza).
// Le card statiche GUIDE_DEFS restano come FALLBACK se il fetch fallisce.
const WP_LANGS = ['en', 'es', 'fr', 'de'];

const langFromLink = (link) => {
  try {
    const path = new URL(link).pathname;
    const first = path.split('/').filter(Boolean)[0];
    return WP_LANGS.includes(first) ? first : 'it';
  } catch (e) { return 'it'; }
};

const decodeEntities = (s) => {
  const el = document.createElement('textarea');
  el.innerHTML = s || '';
  return el.value;
};

const GuidesIndexPage = () => {
  const { t, i18n } = useTranslation();
  const [wpGuides, setWpGuides] = useState(null); // null = fetch in corso/fallito → fallback statico

  useEffect(() => {
    let alive = true;
    fetch('/wp-json/wp/v2/guides?per_page=100&_fields=id,slug,title,excerpt,link')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('http ' + r.status))))
      .then((rows) => { if (alive && Array.isArray(rows) && rows.length) setWpGuides(rows); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const uiLang = (i18n.language || 'it').slice(0, 2);
  const effLang = WP_LANGS.includes(uiLang) ? uiLang : 'it';

  let GUIDES;
  if (wpGuides) {
    GUIDES = wpGuides
      .filter((g) => langFromLink(g.link) === effLang)
      .map((g) => ({
        slug: g.slug,
        href: effLang === 'it' ? `/guide/${g.slug}` : `/${effLang}/guide/${g.slug}`,
        icon: '📘',
        readTime: null,
        eyebrow: t('guides.indexBadge'),
        title: decodeEntities(g.title && g.title.rendered),
        subtitle: decodeEntities(((g.excerpt && g.excerpt.rendered) || '').replace(/<[^>]+>/g, '')).trim(),
      }));
  } else {
    GUIDES = GUIDE_DEFS.map((g) => ({
      ...g,
      href: `/guide/${g.slug}`,
      eyebrow: t(`guidesIndex.guides.${g.slug}.eyebrow`),
      title: t(`guidesIndex.guides.${g.slug}.title`),
      subtitle: t(`guidesIndex.guides.${g.slug}.subtitle`),
    }));
  }

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = t('guides.indexTitle') + ' · AEO Orchestra';
      window.scrollTo(0, 0);
    }
  }, [t]);

  return (
    <>
      <Header />
      <main className="pt-24 pb-24 bg-[#0a0a0a] min-h-screen">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0055FF]/10 border border-[#0055FF]/20 text-[#0055FF] text-xs font-medium uppercase tracking-wider mb-5">
              <BookOpen size={12} /> {t('guides.indexBadge')}
            </div>
            <h1 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight mb-4">
              {t('guides.indexTitle')}
            </h1>
            <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">
              {t('guides.indexSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {GUIDES.map((g) => (
              <Link
                key={g.slug}
                to={g.href}
                className="group bg-[#111111] border border-white/10 hover:border-[#0055FF]/40 rounded-2xl p-7 transition-all hover:-translate-y-0.5"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-3xl">{g.icon}</div>
                  <div className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-[#A1A1AA] text-[10px] font-medium uppercase tracking-wider">
                    {g.eyebrow}
                  </div>
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-2 leading-snug group-hover:text-[#0055FF] transition-colors">
                  {g.title}
                </h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed mb-5">{g.subtitle}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#666]">{g.readTime ? `⏱ ${g.readTime}` : ''}</span>
                  <span className="inline-flex items-center gap-1 text-[#0055FF] group-hover:gap-2 transition-all">
                    {t('guidesIndex.read')} <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link to="/changelog" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white text-sm transition-colors">
              {t('guides.viewChangelog')}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default GuidesIndexPage;
